/**
 * Gerenciamento de Usuários — redesign moderno
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Typography, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  TextField, InputAdornment, Pagination, Select, MenuItem,
  Tooltip, Avatar, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Alert, Divider, Paper,
} from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import BlockIcon         from '@mui/icons-material/Block';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import PersonAddIcon     from '@mui/icons-material/PersonAdd';
import DownloadIcon      from '@mui/icons-material/Download';
import PeopleIcon        from '@mui/icons-material/People';
import PersonOffIcon     from '@mui/icons-material/PersonOff';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import TrendingDownIcon  from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import adminService from '../../services/adminService';

/* ─── palette ─── */
const ORANGE  = '#F97316';
const ORANGE_L = '#FB923C';
const BLUE    = '#2563EB';
const GREEN   = '#10B981';
const RED     = '#EF4444';
const AMBER   = '#F59E0B';

const fmt = (v) =>
  `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

/* ─── Chip de tipo ─── */
const TipoChip = ({ tipo }) => {
  const map = {
    PF:      { label: 'PF',      bg: 'rgba(249,115,22,0.1)', color: ORANGE },
    PJ:      { label: 'PJ',      bg: 'rgba(37,99,235,0.1)', color: BLUE  },
    HIBRIDO: { label: 'Híbrido', bg: 'rgba(245,158,11,0.1)',color: AMBER  },
  };
  const s = map[tipo] || { label: tipo, bg: '#F1F5F9', color: '#64748B' };
  return (
    <Chip label={s.label} size="small"
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 11, height: 22, border: 'none' }} />
  );
};

/* ─── Tab pill ─── */
const TabPill = ({ label, count, active, color = ORANGE, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: 0.8,
      px: 1.8, py: 1,
      cursor: 'pointer',
      borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
      transition: 'all 0.15s',
      '&:hover': { bgcolor: `${color}08` },
    }}
  >
    <Typography sx={{ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? color : '#64748B', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Box sx={{
      minWidth: 20, height: 18, px: 0.6, borderRadius: 10,
      bgcolor: active ? `${color}15` : '#F1F5F9',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: active ? color : '#94A3B8' }}>{count}</Typography>
    </Box>
  </Box>
);

/* ─── Table helpers ─── */
const TH = ({ children, align = 'left' }) => (
  <TableCell align={align} sx={{
    fontSize: 10, fontWeight: 700, color: '#94A3B8',
    letterSpacing: '0.07em', textTransform: 'uppercase',
    borderBottom: '2px solid #F1F5F9', pb: 1.2,
  }}>
    {children}
  </TableCell>
);

/* ═══════════════════════════════════════════ */
const UsuariosAdmin = () => {
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [usuarios,      setUsuarios]      = useState([]);
  const [tab,           setTab]           = useState(0); // 0=todos 1=ativos 2=inativos
  const [page,          setPage]          = useState(1);
  const [totalPaginas,  setTotalPaginas]  = useState(1);
  const [search,        setSearch]        = useState('');
  const [ordenar,       setOrdenar]       = useState('createdAt');
  const [ordem,         setOrdem]         = useState('DESC');
  const [modalOpen,     setModalOpen]     = useState(false);
  const [usuarioSel,    setUsuarioSel]    = useState(null);
  const [detalhes,      setDetalhes]      = useState(null);
  const [loadingDet,    setLoadingDet]    = useState(false);

  const carregar = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const r = await adminService.listarUsuarios({ page, search, ordenar, ordem });
      if (r?.usuarios) { setUsuarios(r.usuarios); setTotalPaginas(r.totalPaginas); }
      else { setError('Resposta inesperada da API'); setUsuarios([]); }
    } catch { setError('Erro ao carregar usuários'); setUsuarios([]); }
    finally   { setLoading(false); }
  }, [page, search, ordenar, ordem]);

  useEffect(() => { carregar(); }, [carregar]);

  const verDetalhes = async (u) => {
    setUsuarioSel(u); setModalOpen(true); setLoadingDet(true);
    try { const d = await adminService.getUsuarioDetalhes(u.id); setDetalhes(d); }
    finally { setLoadingDet(false); }
  };

  const toggleStatus = async (id) => {
    await adminService.toggleStatusUsuario(id);
    carregar();
    if (detalhes?.usuario?.id === id) setModalOpen(false);
  };

  /* filtro local por tab */
  const lista = usuarios.filter((u) => {
    if (tab === 1) return u.ativo;
    if (tab === 2) return !u.ativo;
    return true;
  });

  const totalAtivos   = usuarios.filter(u => u.ativo).length;
  const totalInativos = usuarios.filter(u => !u.ativo).length;

  return (
    <Box sx={{ pb: 4 }}>

      {/* ── Hero ── */}
      <Box sx={{
        mb: 3, p: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 60%, #0C2340 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', right: -20, top: -20, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(249,115,22,0.07)' }} />
        <Box sx={{ position: 'absolute', right: 110, bottom: -40, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(37,99,235,0.06)' }} />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff', mb: 0.3 }}>
              Gerenciamento de Usuários
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'rgba(147,197,253,0.85)' }}>
              Visualize, filtre e gerencie todos os usuários da plataforma
            </Typography>

            {/* quick stats */}
            <Box sx={{ display: 'flex', gap: 2, mt: 2.5, flexWrap: 'wrap' }}>
              {[
                { Icon: PeopleIcon,   label: 'Total',    val: usuarios.length, color: ORANGE_L },
                { Icon: CheckCircleIcon, label: 'Ativos', val: totalAtivos,    color: '#34D399' },
                { Icon: PersonOffIcon,  label: 'Inativos',val: totalInativos,  color: '#F87171' },
              ].map(({ Icon, label, val, color }) => (
                <Box key={label} sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 2, py: 0.9,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 2,
                }}>
                  <Icon sx={{ fontSize: 16, color }} />
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'rgba(147,197,253,0.7)', lineHeight: 1 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{val}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ações */}
          <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2, fontSize: 13, fontWeight: 600, textTransform: 'none',
                borderColor: 'rgba(255,255,255,0.25)', color: '#fff', py: 1, px: 2,
                '&:hover': { borderColor: ORANGE_L, bgcolor: 'rgba(249,115,22,0.1)' },
              }}
            >
              Exportar
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2, fontSize: 13, fontWeight: 700, textTransform: 'none',
                background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
                boxShadow: '0 3px 10px rgba(249,115,22,0.4)', py: 1, px: 2,
                '&:hover': { boxShadow: '0 4px 16px rgba(249,115,22,0.5)' },
              }}
            >
              Novo Usuário
            </Button>
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Card principal ── */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff', overflow: 'hidden' }}>

        {/* tabs + filtros */}
        <Box sx={{
          display: 'flex', alignItems: 'center',
          borderBottom: '1px solid #F1F5F9',
          px: 2, gap: 0,
          flexWrap: 'wrap',
        }}>
          <TabPill label="Todos"   count={usuarios.length} active={tab === 0} color={ORANGE} onClick={() => setTab(0)} />
          <TabPill label="Ativos"  count={totalAtivos}     active={tab === 1} color={GREEN}  onClick={() => setTab(1)} />
          <TabPill label="Inativos"count={totalInativos}   active={tab === 2} color={RED}    onClick={() => setTab(2)} />

          {/* spacer */}
          <Box sx={{ flex: 1 }} />

          {/* filtros inline */}
          <Box sx={{ display: 'flex', gap: 1, py: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Buscar nome, email ou clínica..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment>,
                sx: { borderRadius: 2, fontSize: 13, minWidth: 240 },
              }}
            />
            <Select
              size="small"
              value={ordenar}
              onChange={(e) => setOrdenar(e.target.value)}
              displayEmpty
              renderValue={(v) => {
                const m = { createdAt: 'Data de Cadastro', nome: 'Nome', email: 'Email', updatedAt: 'Atualização' };
                return <Typography sx={{ fontSize: 12, color: '#64748B' }}>Ordenar: {m[v]}</Typography>;
              }}
              sx={{ borderRadius: 2, fontSize: 12, minWidth: 160 }}
            >
              <MenuItem value="createdAt" sx={{ fontSize: 13 }}>Data de Cadastro</MenuItem>
              <MenuItem value="nome"      sx={{ fontSize: 13 }}>Nome</MenuItem>
              <MenuItem value="email"     sx={{ fontSize: 13 }}>Email</MenuItem>
              <MenuItem value="updatedAt" sx={{ fontSize: 13 }}>Última Atualização</MenuItem>
            </Select>
            <Select
              size="small"
              value={ordem}
              onChange={(e) => setOrdem(e.target.value)}
              sx={{ borderRadius: 2, fontSize: 12, minWidth: 110 }}
            >
              <MenuItem value="DESC" sx={{ fontSize: 13 }}>Decrescente</MenuItem>
              <MenuItem value="ASC"  sx={{ fontSize: 13 }}>Crescente</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* tabela */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress sx={{ color: ORANGE }} size={38} thickness={3} />
              <Typography sx={{ mt: 1.5, color: '#94A3B8', fontSize: 13 }}>Carregando...</Typography>
            </Box>
          </Box>
        ) : (
          <TableContainer>
            <Table
              size="small"
              sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #F1F5F9' } }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                  <TH>Usuário</TH>
                  <TH>Tipo</TH>
                  <TH align="right">Lançamentos</TH>
                  <TH align="right">Análises</TH>
                  <TH align="right">Saldo</TH>
                  <TH>Status</TH>
                  <TH>Cadastro</TH>
                  <TH align="center">Ações</TH>
                </TableRow>
              </TableHead>
              <TableBody>
                {lista.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <PeopleIcon sx={{ fontSize: 40, color: '#E2E8F0', mb: 1 }} />
                      <Typography sx={{ color: '#CBD5E1', fontSize: 13 }}>
                        {search ? 'Nenhum usuário encontrado' : 'Sem usuários nesta categoria'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  lista.map((u) => {
                    const saldo = (u.valorTotalFaturamento || 0) - (u.valorTotalDespesas || 0);
                    return (
                      <TableRow
                        key={u.id}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(249,115,22,0.025)' },
                          transition: 'background 0.12s',
                        }}
                      >
                        {/* usuário */}
                        <TableCell sx={{ py: 1.6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{
                              width: 34, height: 34, fontSize: 13, fontWeight: 700,
                              background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
                            }}>
                              {u.nome?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B', lineHeight: 1.2 }}>
                                {u.nome}
                              </Typography>
                              <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>
                                {u.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* tipo */}
                        <TableCell sx={{ py: 1.4 }}>
                          <TipoChip tipo={u.tipoPessoa} />
                        </TableCell>

                        {/* lançamentos */}
                        <TableCell align="right" sx={{ py: 1.4 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                            {(u.totalFaturamentos || 0) + (u.totalDespesas || 0)}
                          </Typography>
                        </TableCell>

                        {/* análises */}
                        <TableCell align="right" sx={{ py: 1.4 }}>
                          <Typography sx={{ fontSize: 13, color: '#475569' }}>
                            {u.totalAnalises || 0}
                          </Typography>
                        </TableCell>

                        {/* saldo */}
                        <TableCell align="right" sx={{ py: 1.4 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: saldo >= 0 ? GREEN : RED }}>
                            {fmt(saldo)}
                          </Typography>
                        </TableCell>

                        {/* status */}
                        <TableCell sx={{ py: 1.4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: u.ativo ? GREEN : '#CBD5E1' }} />
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: u.ativo ? GREEN : '#94A3B8' }}>
                              {u.ativo ? 'Ativo' : 'Inativo'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* cadastro */}
                        <TableCell sx={{ py: 1.4 }}>
                          <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{fmtDate(u.createdAt)}</Typography>
                        </TableCell>

                        {/* ações */}
                        <TableCell align="center" sx={{ py: 1.4 }}>
                          <Tooltip title="Ver detalhes">
                            <IconButton size="small" onClick={() => verDetalhes(u)}
                              sx={{ color: ORANGE, '&:hover': { bgcolor: 'rgba(249,115,22,0.08)' } }}>
                              <VisibilityIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={u.ativo ? 'Desativar' : 'Ativar'}>
                            <IconButton size="small" onClick={() => toggleStatus(u.id)}
                              sx={{
                                color: u.ativo ? RED : GREEN,
                                '&:hover': { bgcolor: u.ativo ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)' },
                              }}>
                              {u.ativo
                                ? <BlockIcon sx={{ fontSize: 17 }} />
                                : <CheckCircleIcon sx={{ fontSize: 17 }} />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* paginação */}
        {!loading && totalPaginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
            <Pagination
              count={totalPaginas} page={page}
              onChange={(_, v) => setPage(v)}
              size="small"
              sx={{
                '& .MuiPaginationItem-root': { fontSize: 12 },
                '& .Mui-selected': { bgcolor: `${ORANGE} !important`, color: '#fff' },
              }}
            />
          </Box>
        )}
      </Card>

      {/* ── Modal detalhes ── */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, border: '1px solid #E2E8F0' } }}
      >
        <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {usuarioSel && (
              <Avatar sx={{ background: `linear-gradient(135deg, ${ORANGE}, ${ORANGE_L})`, width: 40, height: 40, fontWeight: 700 }}>
                {usuarioSel.nome?.charAt(0).toUpperCase()}
              </Avatar>
            )}
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0F172A', lineHeight: 1.2 }}>
                {usuarioSel?.nome}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{usuarioSel?.email}</Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: usuarioSel?.ativo ? GREEN : '#CBD5E1' }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: usuarioSel?.ativo ? GREEN : '#94A3B8' }}>
                {usuarioSel?.ativo ? 'Ativo' : 'Inativo'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {loadingDet ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: ORANGE }} size={36} />
            </Box>
          ) : detalhes ? (
            <Grid container spacing={2.5}>
              {/* info */}
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
                  Dados do Usuário
                </Typography>
                {[
                  ['Tipo de Pessoa', <TipoChip tipo={detalhes.usuario.tipoPessoa} />],
                  ['Cadastrado em',  <Typography sx={{ fontSize: 13, color: '#1E293B' }}>{fmtDate(detalhes.usuario.createdAt)}</Typography>],
                  ['Clínica',        <Typography sx={{ fontSize: 13, color: '#1E293B' }}>{detalhes.usuario.nomeClinica || '—'}</Typography>],
                ].map(([label, node]) => (
                  <Box key={label} sx={{ mb: 1.8 }}>
                    <Typography sx={{ fontSize: 11, color: '#94A3B8', mb: 0.4 }}>{label}</Typography>
                    {node}
                  </Box>
                ))}
              </Grid>

              {/* stats */}
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
                  Estatísticas
                </Typography>
                <Paper elevation={0} sx={{ bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0', p: 2 }}>
                  {[
                    ['Faturamentos', detalhes.estatisticas.totalFaturamentos, ORANGE],
                    ['Despesas',     detalhes.estatisticas.totalDespesas,     AMBER ],
                    ['Análises',     detalhes.estatisticas.totalAnalises,     BLUE  ],
                  ].map(([l, v, c]) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: 13, color: '#64748B' }}>{l}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: c }}>{v}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingUpIcon sx={{ fontSize: 14, color: GREEN }} />
                      <Typography sx={{ fontSize: 13, color: '#64748B' }}>Faturamento</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: GREEN }}>
                      {fmt(detalhes.estatisticas.valorTotalFaturamento)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingDownIcon sx={{ fontSize: 14, color: RED }} />
                      <Typography sx={{ fontSize: 13, color: '#64748B' }}>Despesas</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: RED }}>
                      {fmt(detalhes.estatisticas.valorTotalDespesas)}
                    </Typography>
                  </Box>
                  <Box sx={{
                    display: 'flex', justifyContent: 'space-between',
                    pt: 1.2, borderTop: '1px solid #E2E8F0',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccountBalanceWalletIcon sx={{ fontSize: 14, color: detalhes.estatisticas.saldo >= 0 ? BLUE : RED }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>Saldo</Typography>
                    </Box>
                    <Typography sx={{
                      fontSize: 14, fontWeight: 800,
                      color: detalhes.estatisticas.saldo >= 0 ? GREEN : RED,
                    }}>
                      {fmt(detalhes.estatisticas.saldo)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Typography sx={{ color: '#94A3B8', textAlign: 'center', py: 3 }}>Erro ao carregar detalhes</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setModalOpen(false)} variant="outlined" size="small"
            sx={{ borderRadius: 2, borderColor: '#E2E8F0', color: '#64748B', fontSize: 13, textTransform: 'none' }}>
            Fechar
          </Button>
          {usuarioSel && (
            <Button
              onClick={() => toggleStatus(usuarioSel.id)}
              variant="contained" size="small"
              sx={{
                borderRadius: 2, fontSize: 13, textTransform: 'none', fontWeight: 600,
                bgcolor: usuarioSel.ativo ? RED : GREEN,
                '&:hover': { bgcolor: usuarioSel.ativo ? '#DC2626' : '#059669' },
              }}
            >
              {usuarioSel.ativo ? 'Desativar usuário' : 'Ativar usuário'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsuariosAdmin;
